/**
 * Celeste Pawn Component
 *
 * SVG character pawn used by the celeste skin. Different structure from basics pawns:
 * a round mascot character with eyes and smile instead of a soccer ball on a tiered base.
 *
 * Color-specific gradients are parameterized via CelestePawnColorScheme.
 * Neutral elements (eyes, smile, shadow) are shared across all color variants.
 *
 * ViewBox: 0 0 323 473 (matches basics pawn for consistent road section scaling)
 */

import React from 'react';
import type { PawnProps } from '../../constants/types';
import type { CelestePawnColorScheme } from './constants/pawnColorSchemes';

interface CelestePawnInternalProps extends PawnProps {
  colorScheme: CelestePawnColorScheme;
}

const CelestePawnComponent = React.memo(function CelestePawnComponent({
  viewBox = '0 0 323 473',
  className = '',
  colorScheme
}: CelestePawnInternalProps) {
  const prefix = `${colorScheme.name}CelestePawn`;

  return (
    <svg
      viewBox={viewBox}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${colorScheme.name} celeste pawn`}
    >
      {/* Outer shell shadow */}
      <path
        d="M155.11 448.91C89.7401 448.91 36.5601 399.57 36.5601 338.92C36.5601 323.91 39.7601 309.36 46.0701 295.67C48.5801 274.09 57.2201 254.04 71.1101 237.5C69.9601 236.24 68.8401 234.94 67.7101 233.56C56.5301 219.97 48.7601 203.73 45.2401 186.59C43.5101 178.27 42.7701 169.65 43.0501 160.98C43.6101 143.93 48.0001 127.73 56.0901 112.83C63.8901 98.42 75.0901 85.74 88.4801 76.16C107 62.89 128.83 55.88 151.61 55.88C152.82 55.88 154.03 55.9 155.25 55.94C162.49 56.19 169.57 57.12 176.31 58.7C182.69 60.18 188.96 62.24 194.93 64.84C200.71 67.34 206.41 70.45 211.87 74.06C222.12 80.88 231.08 89.32 238.5 99.17C245.99 109.11 251.67 120.14 255.38 131.97L255.52 132.41V132.43C256.03 134.05 256.51 135.76 257 137.63C259.23 146.38 260.36 155.41 260.36 164.49C260.36 165.7 260.34 166.91 260.3 168.13C259.76 184.75 255.57 200.59 247.86 215.2C245.94 218.86 243.76 222.46 241.39 225.91C239.22 229.07 236.9 232.11 234.45 234.97C247.96 250.12 256.98 268.6 260.61 288.63C269.16 304.08 273.68 321.45 273.68 338.9C273.68 399.55 220.5 448.89 155.13 448.89L155.11 448.91Z"
        fill={`url(#${prefix}_shadow)`}
      />

      {/* Body highlight circle */}
      <path
        d="M155.11 445.92C218.932 445.92 270.67 398.01 270.67 338.91C270.67 279.81 218.932 231.9 155.11 231.9C91.288 231.9 39.55 279.81 39.55 338.91C39.55 398.01 91.288 445.92 155.11 445.92Z"
        fill={colorScheme.bodyHighlight}
      />

      {/* Back body gradient circle */}
      <path
        d="M153.85 425.88C212.138 425.88 259.39 377.97 259.39 318.87C259.39 259.77 212.138 211.86 153.85 211.86C95.5619 211.86 48.3101 259.77 48.3101 318.87C48.3101 377.97 95.5619 425.88 153.85 425.88Z"
        fill={`url(#${prefix}_backBody)`}
      />

      {/* Front body gradient circle */}
      <path
        d="M153.85 415.71C212.138 415.71 259.39 367.8 259.39 308.7C259.39 249.6 212.138 201.69 153.85 201.69C95.5619 201.69 48.3101 249.6 48.3101 308.7C48.3101 367.8 95.5619 415.71 153.85 415.71Z"
        fill={`url(#${prefix}_frontBody)`}
      />

      {/* Face circle */}
      <path
        d="M155.48 370.65C115.73 370.65 83.3901 337.66 83.3901 297.11C83.3901 256.56 115.73 223.57 155.48 223.57C195.23 223.57 227.57 256.56 227.57 297.11C227.57 337.66 195.23 370.65 155.48 370.65Z"
        fill={colorScheme.faceCircle}
      />

      {/* Face shadow ring (black) */}
      <path
        d="M155.11 368.64C196.515 368.64 230.08 334.609 230.08 292.63C230.08 250.651 196.515 216.62 155.11 216.62C113.705 216.62 80.1401 250.651 80.1401 292.63C80.1401 334.609 113.705 368.64 155.11 368.64Z"
        fill="black"
      />

      {/* Face shadow inner (black 57% opacity) */}
      <path
        opacity="0.57"
        d="M155.11 364.47C196.515 364.47 230.08 330.439 230.08 288.46C230.08 246.481 196.515 212.45 155.11 212.45C113.705 212.45 80.1401 246.481 80.1401 288.46C80.1401 330.439 113.705 364.47 155.11 364.47Z"
        fill="black"
      />

      {/* Front section highlight (upper body/shirt shadow) */}
      <path
        d="M73.6002 223.06L107.5 316.5C114.34 335.34 131.95 348.13 151.99 348.79C174.53 349.53 194.67 334.8 200.8 313.1L226.83 220.89C235.55 190 212.73 159.19 180.63 158.53L120.7 157.3C86.2602 156.6 61.8502 190.68 73.5902 223.06H73.6002Z"
        fill={colorScheme.frontHighlight}
      />

      {/* Front section gradient (upper body/shirt) */}
      <path
        d="M73.6002 217.79L107.5 311.23C114.34 330.07 131.95 342.86 151.99 343.52C174.53 344.26 194.67 329.53 200.8 307.83L226.83 215.62C235.55 184.73 212.73 153.92 180.63 153.26L120.7 152.03C86.2602 151.33 61.8502 185.41 73.5902 217.79H73.6002Z"
        fill={`url(#${prefix}_shirt)`}
      />

      {/* Hair/top gradient (neutral) */}
      <path
        d="M274.83 215.13C247.09 246.3 199.33 249.08 168.16 221.34C167.8 221.02 167.44 220.7 167.09 220.38C165.41 222.79 163.57 225.13 161.57 227.38C133.83 258.55 86.0699 261.34 54.8899 233.6C25.4599 207.41 21.3399 163.36 44.3399 132.29C40.1899 130.34 36.2399 127.76 32.6299 124.55C12.4599 106.6 10.6599 75.7 28.6099 55.53C46.5599 35.36 77.4599 33.56 97.6299 51.51C125.36 20.34 173.12 17.55 204.29 45.3C217.91 57.42 226.11 73.37 228.71 90.07C243.05 92.03 256.98 98.13 268.6 108.47C299.77 136.21 302.56 183.97 274.82 215.15L274.83 215.13Z"
        fill={`url(#${prefix}_hair)`}
      />

      {/* Left eye */}
      <path
        d="M110.01 147.75C120.017 147.75 128.13 139.637 128.13 129.63C128.13 119.623 120.017 111.51 110.01 111.51C100.002 111.51 91.8899 119.623 91.8899 129.63C91.8899 139.637 100.002 147.75 110.01 147.75Z"
        fill={`url(#${prefix}_eyeLeft)`}
      />

      {/* Right eye */}
      <path
        d="M190.77 147.75C200.777 147.75 208.89 139.637 208.89 129.63C208.89 119.623 200.777 111.51 190.77 111.51C180.763 111.51 172.65 119.623 172.65 129.63C172.65 139.637 180.763 147.75 190.77 147.75Z"
        fill={`url(#${prefix}_eyeRight)`}
      />

      {/* Smile */}
      <path
        d="M128.14 152.38C128.45 153.44 129.01 154.93 130.02 156.51C133.92 162.61 140.9 164.21 143.35 164.77C151.9 166.74 162.38 164.52 168.7 157.45C170.39 155.56 171.44 153.7 172.08 152.38"
        stroke="black"
        strokeWidth="8"
        strokeMiterlimit="10"
        strokeLinecap="round"
        fill="none"
      />

      <defs>
        {/* Shadow gradient (neutral) */}
        <linearGradient
          id={`${prefix}_shadow`}
          x1="155.11"
          y1="61.11"
          x2="155.11"
          y2="437.37"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.54" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Back body gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_backBody`}
          x1="48.3201"
          y1="318.87"
          x2="259.39"
          y2="318.87"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.backBody[0]} />
          <stop offset="0.48" stopColor={colorScheme.backBody[1]} />
          <stop offset="1" stopColor={colorScheme.backBody[2]} />
        </linearGradient>

        {/* Front body gradient (color-specific) */}
        <linearGradient
          id={`${prefix}_frontBody`}
          x1="48.3201"
          y1="308.7"
          x2="259.39"
          y2="308.7"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.frontBody[0]} />
          <stop offset="0.48" stopColor={colorScheme.frontBody[1]} />
          <stop offset="1" stopColor={colorScheme.frontBody[2]} />
        </linearGradient>

        {/* Shirt gradient (color-specific, same values as front body) */}
        <linearGradient
          id={`${prefix}_shirt`}
          x1="70.6202"
          y1="247.78"
          x2="228.71"
          y2="247.78"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.frontBody[0]} />
          <stop offset="0.48" stopColor={colorScheme.frontBody[1]} />
          <stop offset="1" stopColor={colorScheme.frontBody[2]} />
        </linearGradient>

        {/* Hair gradient (neutral) */}
        <linearGradient
          id={`${prefix}_hair`}
          x1="31.3533"
          y1="52.0926"
          x2="248.009"
          y2="244.923"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EFEDEE" />
          <stop offset="1" stopColor="#A6B6CF" />
        </linearGradient>

        {/* Left eye radial gradient (neutral) */}
        <radialGradient
          id={`${prefix}_eyeLeft`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(117.71 124.47) scale(16.94 16.94)"
        >
          <stop stopColor="white" />
          <stop offset="0.13" stopColor="#BFBFBF" />
          <stop offset="0.25" stopColor="#858585" />
          <stop offset="0.38" stopColor="#565656" />
          <stop offset="0.5" stopColor="#303030" />
          <stop offset="0.61" stopColor="#151515" />
          <stop offset="0.7" stopColor="#050505" />
          <stop offset="0.78" stopColor="black" />
          <stop offset="1" stopColor="black" />
        </radialGradient>

        {/* Right eye radial gradient (neutral) */}
        <radialGradient
          id={`${prefix}_eyeRight`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(198.47 124.47) scale(16.94 16.94)"
        >
          <stop stopColor="white" />
          <stop offset="0.13" stopColor="#BFBFBF" />
          <stop offset="0.25" stopColor="#858585" />
          <stop offset="0.38" stopColor="#565656" />
          <stop offset="0.5" stopColor="#303030" />
          <stop offset="0.61" stopColor="#151515" />
          <stop offset="0.7" stopColor="#050505" />
          <stop offset="0.78" stopColor="black" />
          <stop offset="1" stopColor="black" />
        </radialGradient>
      </defs>
    </svg>
  );
});

CelestePawnComponent.displayName = 'CelestePawnComponent';

export default CelestePawnComponent;
